import { useEffect, useState, useRef } from 'react';

interface UseAlertProps {
  open: boolean;
  externalIsLeaving?: boolean;
}

export const useAlert = ({ open, externalIsLeaving }: UseAlertProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [internalIsLeaving, setInternalIsLeaving] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutIdRef = useRef(0);

  // Determinar el estado de salida: usar el externo si está disponible, sino el interno
  const isLeaving = externalIsLeaving !== undefined ? externalIsLeaving : internalIsLeaving;

  // Efecto principal: maneja open y externalIsLeaving
  useEffect(() => {
    const currentTimeoutId = ++timeoutIdRef.current;

    // Limpiar timeout anterior si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (open) {
      setIsVisible(true);
      setInternalIsLeaving(false);
    } else {
      setInternalIsLeaving(true);
      // Solo usar timeout si no hay control externo
      if (externalIsLeaving === undefined) {
        timeoutRef.current = setTimeout(() => {
          // Solo ejecutar si este es el timeout más reciente
          if (timeoutIdRef.current === currentTimeoutId) {
            setIsVisible(false);
            timeoutRef.current = null;
          }
        }, 300);
      }
    }

    // Cleanup: limpiar timeout solo si ya no es el más reciente
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- comparar id en cleanup es intencional
      const idAtCleanup = timeoutIdRef.current;
      if (timeoutRef.current && idAtCleanup !== currentTimeoutId) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [open, externalIsLeaving]);

  // Efecto secundario: maneja cuando externalIsLeaving cambia independientemente de open
  useEffect(() => {
    // Solo procesar si externalIsLeaving está definido y es true (indicando que debe salir)
    if (externalIsLeaving === undefined || !externalIsLeaving) {
      return;
    }

    // Solo procesar si el estado interno no coincide
    if (externalIsLeaving === internalIsLeaving) {
      return;
    }

    const currentTimeoutId = ++timeoutIdRef.current;

    // Limpiar timeout anterior si existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    setInternalIsLeaving(true);
    timeoutRef.current = setTimeout(() => {
      // Solo ejecutar si este es el timeout más reciente
      if (timeoutIdRef.current === currentTimeoutId) {
        setIsVisible(false);
        timeoutRef.current = null;
      }
    }, 300);

    // Cleanup: limpiar timeout solo si ya no es el más reciente
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- comparar id en cleanup es intencional
      const idAtCleanup = timeoutIdRef.current;
      if (timeoutRef.current && idAtCleanup !== currentTimeoutId) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [externalIsLeaving, internalIsLeaving]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  return {
    isVisible,
    isLeaving,
  };
};
